import * as React from "react";

/**
 * Multi-line text input with optional character counter.
 * Ported from the Wanted Design System bundle (components/forms/Textarea.jsx).
 * Tellvue additions: `counter` replaces the default "n/max" line (the customer page counts
 * against a platform limit without truncating), `fontSize` for the 17px customer body, and
 * aria-describedby on the helper line.
 */
export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  helperText?: React.ReactNode;
  error?: boolean;
  disabled?: boolean;
  rows?: number;
  maxLength?: number;
  counter?: React.ReactNode;
  fontSize?: string;
  ref?: React.Ref<HTMLTextAreaElement>;
}

export function Textarea({
  label,
  value,
  placeholder,
  helperText,
  error = false,
  disabled = false,
  rows = 4,
  maxLength,
  counter,
  fontSize = "var(--font-size-body-2)",
  style,
  onFocus,
  onBlur,
  ...rest
}: TextareaProps) {
  const [focused, setFocused] = React.useState(false);
  const helperId = React.useId();
  const count = String(value ?? "").length;
  const counterNode = counter ?? (maxLength ? `${count}/${maxLength}` : null);
  return (
    <label
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        minWidth: 0,
        fontFamily: "var(--font-sans)",
        ...style,
      }}
    >
      {label && (
        <span
          style={{
            fontSize: "var(--font-size-label-1)",
            lineHeight: "var(--line-height-label-1)",
            fontWeight: 600,
            color: "var(--label-normal)",
          }}
        >
          {label}
        </span>
      )}
      <div
        style={{
          padding: 14,
          borderRadius: "var(--radius-md)",
          background: disabled
            ? "var(--background-normal-alternative)"
            : "var(--background-normal-normal)",
          boxShadow: `inset 0 0 0 ${focused && !error ? 1.5 : 1}px ${
            error
              ? "var(--status-negative)"
              : focused
                ? "var(--primary-normal)"
                : "var(--line-normal-normal)"
          }`,
          transition: "box-shadow var(--duration-fast) var(--ease-standard)",
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <textarea
          value={value}
          placeholder={placeholder}
          rows={rows}
          maxLength={maxLength}
          disabled={disabled}
          aria-invalid={error || undefined}
          aria-describedby={helperText || counterNode ? helperId : undefined}
          onFocus={(e) => {
            setFocused(true);
            onFocus?.(e);
          }}
          onBlur={(e) => {
            setFocused(false);
            onBlur?.(e);
          }}
          style={{
            display: "block",
            width: "100%",
            border: "none",
            outline: "none",
            resize: "vertical",
            background: "transparent",
            fontFamily: "var(--font-sans)",
            fontSize,
            lineHeight: 1.6,
            color: "var(--label-normal)",
            boxSizing: "border-box",
          }}
          {...rest}
        />
      </div>
      {(helperText || counterNode) && (
        <div
          id={helperId}
          style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}
        >
          <span
            style={{
              fontSize: "var(--font-size-label-2)",
              color: error ? "var(--status-negative)" : "var(--label-neutral)",
            }}
          >
            {helperText || ""}
          </span>
          {counterNode && (
            <span
              style={{
                fontSize: "var(--font-size-label-2)",
                color: error ? "var(--status-negative)" : "var(--label-neutral)",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {counterNode}
            </span>
          )}
        </div>
      )}
    </label>
  );
}

export default Textarea;
